/**
 * QuickScore brand colors — shared by every branded QuickScore email (welcome, OTP, results
 * export) and by the self-contained HTML reports the benchmark CLI writes.
 *
 * COUPLING: QUICKSCORE_BRAND_GREEN's hex must match `--sage` in
 * apps/quickscore/website/src/brand.css, `$primary` in
 * apps/quickscore/admin/src/assets/css/bootstrap5.scss, `BRAND_PRIMARY_COLOR` in
 * apps/quickscore/mobile/app.config.js, and `lightPaperTheme.colors.primary` in
 * apps/quickscore/mobile/src/paperTheme.ts — a stylesheet cannot import a TS const, and neither
 * the mobile app's Metro bundler nor that plain CommonJS Expo config (no `require` at all) can
 * load this package's `.ts` entry point.
 */
export const QUICKSCORE_BRAND_GREEN = '#50783F' // white at 5.123:1 on this green
export const QUICKSCORE_BRAND_CREAM = '#F7F6D3'
export const QUICKSCORE_BRAND_BROWN = '#4A3B3B'

// Public QuickScore logo on the shared DigitalOcean Spaces bucket (panelpunches/quickscore/),
// served via the Spaces CDN. Static by design — an email <img> needs an absolute, always-public URL.
export const QUICKSCORE_EMAIL_LOGO_URL =
  'https://panelpunches.nyc3.cdn.digitaloceanspaces.com/quickscore/email-logo.png'

export type BuildQuickscoreEmailArgs = {
  title: string
  bodyHtml: string
}

/**
 * Wraps `bodyHtml` in the shared QuickScore branded layout — a green header band with the
 * hosted logo + title, a white body card, and a brown "QuickScore" footer. Mirrors the
 * structure/colors of the original results-export email so the welcome, OTP-code, and
 * results-export emails all render from one layout; each caller supplies only its own
 * content via `bodyHtml`.
 */
export const buildQuickscoreEmail = ({ title, bodyHtml }: BuildQuickscoreEmailArgs): string => {
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; margin: 0; padding: 0; background-color: ${QUICKSCORE_BRAND_CREAM};">
  <div style="max-width: 700px; margin: 0 auto; padding: 20px;">
    <!-- Header -->
    <div style="background-color: ${QUICKSCORE_BRAND_GREEN}; padding: 24px; border-radius: 12px 12px 0 0; text-align: center;">
      <img src="${QUICKSCORE_EMAIL_LOGO_URL}" alt="QuickScore" style="height: 40px; margin-bottom: 12px;" />
      <h1 style="color: #FFFFFF; margin: 0; font-size: 24px;">${title}</h1>
    </div>

    <!-- Body -->
    <div style="background-color: #FFFFFF; padding: 24px;">
      ${bodyHtml}
    </div>

    <!-- Footer -->
    <div style="background-color: ${QUICKSCORE_BRAND_BROWN}; padding: 20px; border-radius: 0 0 12px 12px; text-align: center;">
      <p style="color: #FFFFFF; margin: 0; font-size: 14px;">
        Generated by <strong>QuickScore</strong>
      </p>
    </div>
  </div>
</body>
</html>
  `
}
